import { useEffect } from "react";
import _refiner from "refiner-js";
import { useQueryUserRefinerSignature } from "src/api/users/query";
import useAuth from "src/hooks/useAuth";
import { getLocale } from "src/i18n";
import { LanguageEnum, LocaleEnum } from "src/types/enum/LanguageEnum";

const MISSING_NPS_PROJECT_ID = "missing-project-id";
const REFINER_PROJECT_ID = import.meta.env.VITE_NPS_PROJECT_ID ?? MISSING_NPS_PROJECT_ID;

const LOCALE_TO_REFINER_COUNTRY = {
    [LanguageEnum.FR]: LocaleEnum.FR,
    [LanguageEnum.GB]: LocaleEnum.EN,
    [LanguageEnum.ES]: LocaleEnum.ES,
};

export const useRefiner = (): void => {
    const { user, userAdminId } = useAuth();
    const { data } = useQueryUserRefinerSignature(user?.id);
    const signature = data?.signature ?? null;

    useEffect(() => {
        if (!user) {
            // biome-ignore lint/suspicious/noConsole: legacy
            console.log("useRefiner - User is not logged in, no need to set up Refiner");
            return;
        }

        if (userAdminId) {
            // biome-ignore lint/suspicious/noConsole: legacy
            console.log("useRefiner - You are an admin, no need to set up Refiner");
            return;
        }

        if (REFINER_PROJECT_ID === MISSING_NPS_PROJECT_ID) {
            console.error("useRefiner - VITE_NPS_PROJECT_ID is missing");
            return;
        }

        if (!signature) {
            return;
        }

        const locale = getLocale();

        const userIdString = user.id.toString();

        const refinerConfig = {
            email: user.email,
            id: userIdString,
            name: `${user.firstName} ${user.lastName}`,
        };

        // Create signature asynchronously using Web Crypto API
        const refinerLocale = {
            locale: LOCALE_TO_REFINER_COUNTRY[locale],
            country: LOCALE_TO_REFINER_COUNTRY[locale],
            signature,
        };

        _refiner("setProject", REFINER_PROJECT_ID);
        _refiner("identifyUser", refinerConfig, refinerLocale);
    }, [user, userAdminId, signature]);
};
